import { FC } from 'react'

import { Modal, ModalProps } from 'antd'
import styled from 'styled-components'
import { Controller, useForm } from 'react-hook-form'
import { object as yupObject, string as yupString } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

import { PrimaryDottedButton } from 'src/Components/Buttons/PrimaryDottedButton'
import { PrimaryInput } from 'src/Components/PrimaryInput'

import { CouponApplyResult, ScheduleSummaryProps } from '..'
import { numberToMoney } from '../../../Constants/functions'

const StyledModal = styled(Modal)`
  width: fit-content !important;
  width: 280px;
  .ant-modal-content {
    display: flex;
    width: 280px;
    padding: 16px;
    flex-direction: column;
    gap: 16px;
    border-radius: 16px;
    border: 1px solid ${({ theme }) => theme.color.lightGrey};
    background: ${({ theme }) => theme.color.white};
    box-shadow: ${({ theme }) => theme.shadow.header};
  }

  .ant-modal-close-icon {
    svg {
      path {
        fill: ${({ theme }) => theme.color.darkGrey};
      }
    }
  }
`
const PromocodeForm = styled.form`
  display: flex;
  flex-direction: column;
  padding-top: 16px;
  gap: 16px;
`
const Title = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
`
interface FormDataProps {
  coupon: string
}

const schema = yupObject().shape({
  coupon: yupString().trim().required(),
})

const StyledPrimaryDottedButton = styled(PrimaryDottedButton)`
  width: fit-content;
  margin: 0 auto;
`

const COUPON_ERROR_MAP: Record<string, string> = {
  'Coupon usage limit exceeded':
    'This code has already been used. Please try another one.',
  'Coupon has expired': 'This promo code has expired. Please try another one.',
}

const DEFAULT_COUPON_ERROR_MESSAGE =
  'This code is not valid. Please try another one.'

const MINIMUM_SPEND_ERROR = 'Minimum spend requirement not met for coupon'

const getErrorMessage = (
  error: CouponApplyResult['coupon']['error'],
): string => {
  if (!error) {
    return DEFAULT_COUPON_ERROR_MESSAGE
  }

  if (error.message === MINIMUM_SPEND_ERROR) {
    if (
      error.param &&
      (typeof error.param === 'string' || typeof error.param === 'number')
    ) {
      return `You need to spend at least $${numberToMoney(Number(error.param))} to use this coupon.`
    }
    return MINIMUM_SPEND_ERROR
  }

  return COUPON_ERROR_MAP[error.message] || DEFAULT_COUPON_ERROR_MESSAGE
}

type Props = ModalProps &
  Pick<ScheduleSummaryProps, 'onApplyPromocode'> & {
    onCancel: () => void
    onClose: () => void
    onBonusCouponApplied?: () => void
  }

export const ScheduleSummaryPromocodePopup: FC<Props> = (props) => {
  const {
    onApplyPromocode,
    onClose,
    onCancel,
    onBonusCouponApplied,
    ...restProps
  } = props

  const { control, handleSubmit, formState, setError, resetField, reset } =
    useForm<FormDataProps>({
      resolver: yupResolver(schema),
      defaultValues: {
        coupon: '',
      },
    })

  const handleSubmitPromoCode = async (
    values: FormDataProps,
    _event?: React.BaseSyntheticEvent,
  ): Promise<void> => {
    if (values.coupon) {
      if (values.coupon.includes('?') || values.coupon.includes('%')) {
        setError('coupon', {
          message: DEFAULT_COUPON_ERROR_MESSAGE,
        })
        return
      }
      onApplyPromocode(values.coupon)
        .then((data) => {
          if (data && data.coupon.error) {
            const errorMessage = getErrorMessage(data.coupon.error)

            setError('coupon', {
              message: errorMessage,
            })
            return
          }

          if (data?.coupon.isBonusCoupon) {
            onBonusCouponApplied?.()
          }
          onClose()
        })
        .catch(() => {
          setError('coupon', {
            message: DEFAULT_COUPON_ERROR_MESSAGE,
          })
        })
    }
  }

  return (
    <StyledModal
      footer={null}
      centered
      {...restProps}
      onCancel={(e) => {
        onCancel?.(e)
        reset()
        resetField('coupon')
      }}
    >
      <Title>Promo Code</Title>
      <PromocodeForm onSubmit={handleSubmit(handleSubmitPromoCode)}>
        <Controller
          name='coupon'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <PrimaryInput
              {...field}
              autoComplete='off'
              placeholder='Enter a promo code'
              error={error?.message}
            />
          )}
        />
        <StyledPrimaryDottedButton disabled={!formState.isValid} type='submit'>
          Apply
        </StyledPrimaryDottedButton>
      </PromocodeForm>
    </StyledModal>
  )
}
